import type { PublicLocation } from './types'

/**
 * Fetch the public location directory — GET {apiBase}/api/locations (no auth).
 *
 * `apiBase` lets each host point at the right API origin: the account portal
 * hits the deployed Worker cross-origin (its VITE_API_BASE), while the consumer
 * checkout is same-origin ('' in prod resolves relative; its dev stub intercepts
 * `/api/locations`). Throws a plain Error carrying the server's `{ error }`
 * message (or a generic fallback) so the caller can surface it verbatim.
 */
export async function fetchLocations(apiBase = ''): Promise<PublicLocation[]> {
  // Normalize a trailing slash so a `VITE_API_BASE` typo can't produce
  // `//api/locations`, and bound the request so a stalled connection surfaces an
  // error (→ Retry) instead of spinning the loading UI forever.
  const base = apiBase.replace(/\/+$/, '')
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10_000)
  let res: Response
  try {
    res = await fetch(`${base}/api/locations`, { method: 'GET', signal: controller.signal })
  } catch (err) {
    // The timeout abort surfaces as an unfriendly DOMException — normalize it.
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Timed out loading locations. Check your connection and try again.')
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
  if (!res.ok) {
    let detail = `Request failed (${res.status})`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) detail = body.error
    } catch {
      /* non-JSON error body — keep the generic detail */
    }
    throw new Error(detail)
  }
  const data = (await res.json()) as { locations?: PublicLocation[] }
  return data.locations ?? []
}
